import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { heroImage } from "@/data/products";
import { WA_LINK } from "@/i18n";
import Bubbles from "@/components/Bubbles";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.13, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  const scrollToCatalog = () => {
    const el = document.getElementById("koleksi");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  };

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="noise relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-black pt-24 pb-16"
    >
      <motion.div
        style={{ opacity: glowOpacity }}
        className="pointer-events-none absolute -right-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-ember/20 blur-[140px]"
      />
      <Bubbles />
      <motion.div
        style={{ y: yText }}
        className="pointer-events-none absolute -bottom-10 left-0 select-none font-display text-[22vw] font-black leading-none tracking-tight text-transparent opacity-[0.05] lg:text-[18rem]"
        aria-hidden="true"
      >
        <span style={{ WebkitTextStroke: "1px #FF6B00" }}>NUSUA</span>
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mb-8 inline-flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-ember" />
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-ember">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[4.2rem]">
            {t.hero.lines.map((line, i) => (
              <span key={`${line.text}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineAnim}
                  initial="hidden"
                  animate="show"
                  className={`block ${
                    line.accent
                      ? "bg-gradient-to-r from-ember-light to-ember bg-clip-text font-accent italic text-transparent"
                      : ""
                  }`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-mist"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={scrollToCatalog}
              className="group flex items-center gap-3 rounded-full bg-ember px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_40px_rgba(255,107,0,0.4)]"
            >
              {t.hero.ctaExplore}
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              data-testid="hero-contact-btn"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full border border-white/20 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-ember hover:text-ember"
            >
              <MessageCircle size={15} />
              {t.hero.ctaContact}
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImg }} className="animate-float relative">
              <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-ember/15 blur-3xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_80px_-20px_rgba(255,107,0,0.25)]">
                <img
                  src={heroImage}
                  alt="Nusua Betta — exclusive betta fish"
                  data-testid="hero-image"
                  className="aspect-[4/5] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl border border-white/10 bg-black/60 px-5 py-4 backdrop-blur-md">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-ember">
                    {t.hero.badgeStrain}
                  </p>
                  <p className="mt-1 font-display text-sm font-bold text-white">
                    Giant Black Samurai
                  </p>
                </div>
                <span className="rounded-full bg-ember px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-black">
                  {t.hero.badgeGrade}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-3 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-ember to-transparent"
        />
      </motion.div>
    </section>
  );
}
